import { faPause, faPlay, faVolumeOff, faVolumeUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import './AdvancedMediaViewerVideo.scss';
import AdvancedMediaViewerOverlay from '../AdvancedMediaViewerOverlay/AdvancedMediaViewerOverlay';
import AdvancedMediaViewerPinchToZoom from '../AdvancedMediaViewerPinchToZoom/AdvancedMediaViewerPinchToZoom';
import  { isRatioSimilar, getScaleRatio } from '../../hooks/useAspectRatio';
import useUserInactivity from '../../hooks/useUserInactivity';
import ContentLocked from '../../../ContentLocked/ContentLocked';
import CONTENT_RESTRICTION_RULE from '../../../../../const/contentRestrictionRule';
import useCurrentUser from '../../../../../hooks/useCurrentUser';

const AdvancedMediaViewerVideoControls = ({
    playing, 
    onPlayPause, 
    onSeek, 
    isBuffering,
    totalDuration,
    played,
    isUserInactive,
    onMuteUnmute,
    isMuted,
  }) => {
    const [isSeeking, setIsSeeking] = useState(false);
    const [seeking, setSeeking] = useState(0);

    const sliderRef = useRef(null);
    const sliderMin = 0;
    const sliderMax = 1;

    const handleSliderValue = (e) => {
        const slider = sliderRef.current.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        let newValue = ((clientX - slider.left) / slider.width) * (sliderMax - sliderMin) + sliderMin;
        newValue = Math.min(Math.max(newValue, sliderMin), sliderMax);
        setSeeking(newValue);
    };

    const handleTouchStart = (e) => {
        handleSliderValue(e);
        setIsSeeking(true);
    };

    const handleTouchMove = (e) => {
        if(!isSeeking) setIsSeeking(true);
        handleSliderValue(e);
    };

    const handleTouchEnd = (e) => {
        if(!isSeeking && seeking === 0) return; //cancel double fire
        onSeek(seeking);
        setIsSeeking(false);
        setSeeking(0);
    };

    const handleMouseLeave = () => {
        setIsSeeking(false);
        setSeeking(0);
    };

    

    const internalValue = isSeeking ? seeking : played;
    const playedTime = internalValue * totalDuration;
    return (
      <div className={classNames("amv-media-video-controls", {'user-is-inactive':isUserInactive})}>
        <div onClick={onPlayPause} className='amv-media-video-controls-play-button'>{playing ? <FontAwesomeIcon icon={faPause}/> : <FontAwesomeIcon icon={faPlay}/>}</div>
        <div className="slider-range-wrapper"
            ref={sliderRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseMove={handleTouchMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleTouchEnd}
        >
            <div className={classNames('slider-range', {'is-seeking':isSeeking})}>
                    
                {(isBuffering && !isSeeking) && <div className="is-buffering"/>}
                <div
                    className={classNames('slider-range-fill')}
                    style={{
                        width: `${((internalValue - sliderMin) / (sliderMax - sliderMin)) * 100}%`
                    }}
                />
            </div>
        </div>
        
        <div className='amv-media-video-controls-duration'>
            {`${Math.floor(playedTime / 60)}:${String(Math.floor(playedTime) % 60).padStart(2, '0')}`}/{`${Math.floor(totalDuration / 60)}:${String(Math.floor(totalDuration) % 60).padStart(2, '0')}`}
        </div>
        <div onClick={onMuteUnmute} className='amv-media-video-controls-volume-button'>{<FontAwesomeIcon icon={isMuted ? faVolumeOff : faVolumeUp}/>}</div>
      </div>
    );
  };


const  AdvancedMediaViewerVideo = ({media, isActive, coverMode, setToggleSlider, toggleSlider, handleClose})=>{
    const [playing, setPlaying] = useState(true);
    const [mute, setMute] = useState(true);
    const [played, setPlayed] = useState(0);
    const [isBuffering, setBuffering] = useState(true);
    const [scaleRatio, setScaleRatio] = useState(null);
    const containerRef = useRef(null);
    const currentUser = useCurrentUser();
    const retrainLimitDuration = 30;
    const { isUserInactive, handleInteractionStart, handleInteractionEnd } = useUserInactivity(isActive && !isBuffering);
    const playerRef = useRef(null);

    const handleReady = () => {
        const player = playerRef.current;
        if (player && coverMode) {
            const videoElement = player.getInternalPlayer();
            const width = videoElement.videoWidth;
            const height = videoElement.videoHeight;
            if (containerRef.current) {
                const { clientWidth, clientHeight } = containerRef.current;
                if (clientWidth && clientHeight) {
                    const containerRatio = clientWidth / clientHeight;
                    const playerRatio = (width / height);
                    const scaleRatio = getScaleRatio(playerRatio, containerRatio);
                    const fullScreen = isRatioSimilar(playerRatio, containerRatio, 0.35);
                    if (fullScreen) {
                        setScaleRatio(scaleRatio);
                    }
                }
              }
        }
    };
    const { _id, videoUrlFormat } = media;

    const handlePlayPause = useCallback(() => {
        setPlaying(!playing);
    }, [playing]);

    const handleMuteUnmute = useCallback(() => {
        setMute(!mute);
    }, [mute]);

    const handleProgress = useCallback((progress) => {
        setPlayed(progress.played);
    }, []);

    const handleSeekChange = useCallback((value) => {
        setBuffering(true);
        setPlayed(value);
        playerRef.current.seekTo(parseFloat(value));
    }, []);

    const handleBuffer = useCallback(() => setBuffering(true), []);
    const handleBufferEnd = useCallback(() => setBuffering(false), []);

    const previewUri = media.videoCover || (Array.isArray(media.previewUrl) ? media.previewUrl[0] : media.previewUrl);
    const durationInSeconds = playerRef.current?.getDuration() || 0;
    const isTimeLocked = !currentUser && (played * durationInSeconds) > retrainLimitDuration;
    const play = isActive && playing && !isTimeLocked;
    return (
        <div 
            ref={containerRef} 
            onTouchStart={handleInteractionStart}
            onTouchEnd={handleInteractionEnd}
            onMouseMove={isUserInactive ? handleInteractionStart : handleInteractionEnd}
            onTouchCancel={handleInteractionEnd}
            className={classNames("amv-media-video", {"time-locked": isTimeLocked})}>
            <AdvancedMediaViewerOverlay 
                media={media}
                isUserInactive={isUserInactive && !isTimeLocked}
                sliderEnable={toggleSlider}
                setSliderEnable={setToggleSlider}
                handleCloseAMV={handleClose}
            >
                <AdvancedMediaViewerVideoControls
                    isUserInactive={isUserInactive && !isTimeLocked}
                    playing={play}
                    onPlayPause={handlePlayPause}
                    played={played}
                    isBuffering={isBuffering}
                    onSeek={handleSeekChange}
                    totalDuration={durationInSeconds}
                    onMuteUnmute={handleMuteUnmute}
                    isMuted={mute}
                />
            </AdvancedMediaViewerOverlay>
            {isTimeLocked && <ContentLocked 
                restrictionRule={CONTENT_RESTRICTION_RULE.VIDEO_DURATION_LIMIT}
                />}
            <AdvancedMediaViewerPinchToZoom scaleRatio={scaleRatio} onZoom={(isZooming)=>setToggleSlider(!isZooming)}>
                <ReactPlayer
                    key={`amv-player-${_id || "error"}`}
                    id={`amv-player-${_id || "error"}`}
                    ref={playerRef}
                    playing={play}
                    onProgress={handleProgress}
                    muted={mute}
                    onReady={handleReady}
                    onContextMenu={e => e.preventDefault()}
                    onDragStart={(e)=> e.preventDefault()}
                    light={isActive ? false : <img style={{objectFit:'contain', height:'100%', width:'100%'}} src={`${previewUri}?height=800`} alt='Thumbnail' />}
                    url={videoUrlFormat?.hd}
                    className="react-player"
                    style={{ width: '100%', zIndex:1}}
                    width="100%"
                    height="100%"
                    onBuffer={handleBuffer}
                    onBufferEnd={handleBufferEnd}
                    onPlay={handleBufferEnd}
                    onSeek={handleBufferEnd}
                    controls={false}
                    loop={true}
                    playsinline={true}
                    config={{
                        file: {
                            attributes: {
                                controlsList: 'nodownload'
                            }
                        }
                    }}
                />
            </AdvancedMediaViewerPinchToZoom>            
        </div>
       
    )
}

export default AdvancedMediaViewerVideo;